// AiAssignBox.jsx — "Assign to AI" dropdown: pick which of the workspace's active
// AI agents should take over this conversation. Same Popover-anchored, list-row
// visual language as AssignBox.jsx's Teams/Team Members dropdown, just one section
// and no search (agent rosters are typically small — add a search bar here the same
// way AssignBox.jsx has one if that stops being true).
import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  CircularProgress,
  Fade,
  Skeleton,
} from "@mui/material";
import { stringToColor } from "../../../config/AvatarColor";

const ListItemSkeleton = () => (
  <ListItem sx={{ padding: "6px 10px" }}>
    <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1.25 }} />
    <Skeleton variant="text" width="60%" height={18} />
  </ListItem>
);

const AiAssignBox = ({ agents, loading, assigningId, onSelect }) => {
  // Only agents the workspace can actually hand a conversation to right now —
  // an inactive agent (AgentModel.isActive: false) is excluded the same way
  // enableTakeover itself would reject it server-side (see aiAgentController.js).
  const activeAgents = (agents || []).filter((agent) => agent.isActive !== false);

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: { xs: "260px", sm: "300px", md: "300px", lg: "300px", xl: "300px" },
        maxWidth: { xs: "290px", sm: "320px", md: "320px", lg: "320px", xl: "320px" },
        maxHeight: { xs: "320px", sm: "360px", md: "360px", lg: "360px", xl: "360px" },
        height: "auto",
        display: "flex",
        flexDirection: "column",
        borderRadius: 0,
        overflow: "hidden",
        backgroundColor: (theme) => theme.palette.background.paper,
        boxShadow: "none",
        border: "none",
      }}
    >
      <Box sx={{ px: 1.25, pt: 1.25, pb: 0.4 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: (theme) => theme.palette.text.secondary, fontWeight: 600, fontSize: "12px" }}
        >
          AI Agents
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        <List sx={{ py: 0.5 }}>
          {loading ? (
            <Box sx={{ py: 1 }}>
              {[1, 2, 3].map((item) => (
                <ListItemSkeleton key={item} />
              ))}
            </Box>
          ) : activeAgents.length > 0 ? (
            activeAgents.map((agent, index) => (
              <Fade in={true} key={agent._id} timeout={index * 100}>
                <ListItem disablePadding sx={{ margin: "2px 6px", width: "auto" }}>
                  <ListItemButton
                    sx={{
                      padding: "6px 10px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
                      },
                    }}
                    disabled={Boolean(assigningId)}
                    onClick={() => onSelect(agent)}
                  >
                    <Avatar
                      sx={{
                        bgcolor: stringToColor(agent.name || "AI Agent"),
                        width: 24,
                        height: 24,
                        mr: 1.25,
                        fontSize: "11px",
                        fontWeight: 600,
                      }}
                    >
                      {(agent.name || "A")[0]}
                    </Avatar>
                    <ListItemText
                      primary={agent.name || "AI Agent"}
                      primaryTypographyProps={{ fontSize: "13px", fontWeight: 500, color: "text.primary" }}
                    />
                    {assigningId === agent._id && (
                      <CircularProgress size={14} sx={{ ml: 1, color: "primary.main" }} />
                    )}
                  </ListItemButton>
                </ListItem>
              </Fade>
            ))
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.text.secondary,
                textAlign: "center",
                padding: "16px",
                fontSize: "13px",
                fontStyle: "italic",
              }}
            >
              No active AI agents configured
            </Typography>
          )}
        </List>
      </Box>
    </Box>
  );
};

export default AiAssignBox;
